import { useState } from "react";
import { Spinner } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";

import { useCourses, useMaterials, useMedia, usePartners } from "@/hooks";
import type { AddEditCourse } from "@/types";
import {
  Button,
  ControlledDropzone,
  errorToast,
  Input,
  MultiSelect,
  Select,
  TextArea,
} from "@/ui";
import { convertToOptions } from "@/utils/selectOptions";

interface AddCourseFormProps {
  onClose: () => void;
}

interface FormValues {
  name: string;
  description: string;
  partner_id: string;
  image?: File;
}

export const AddCourseForm = ({ onClose }: AddCourseFormProps) => {
  const { useUpload } = useMedia();
  const { upload, isUploading } = useUpload();
  const queryClient = useQueryClient();

  const { useAddCourse } = useCourses();
  const { mutateAsync: addCourse, isPending } = useAddCourse();

  const { useAllPartners } = usePartners();
  const { data: partnersData } = useAllPartners(1, {}, undefined);
  const partnerOptions = (partnersData?.data ?? []).map((partner) => ({
    value: partner.id,
    label: partner.name,
  }));

  const { useAllMaterials } = useMaterials();
  const { data: materialsData } = useAllMaterials();
  const materialOptions = convertToOptions(materialsData?.data ?? []);

  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);

  const {
    formState: { errors, isValid },
    handleSubmit,
    register,
    control,
  } = useForm<FormValues>({
    mode: "all",
    defaultValues: {
      name: "",
      description: "",
      partner_id: "",
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      const courseData: AddEditCourse = {
        name: data.name,
        description: data.description,
        partner_id: data.partner_id,
        image: null,
        banner: null,
        image_extension: data.image
          ? data.image.name.split(".").pop()
          : undefined,
        material_ids: selectedMaterials,
        related_resources: [],
        additional_resources: [],
      };

      const newCourse = await addCourse(courseData);

      if (data.image && newCourse.id) {
        const result = await upload(
          data.image,
          `/courses/${newCourse.id}/image/upload-url`,
        );
        if (!result.success) {
          errorToast("Failed to upload image. Please try again.");
          return;
        }
      }

      await queryClient.invalidateQueries({
        queryKey: ["courses"],
      });
      onClose();
    } catch (error) {
      errorToast(error);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex w-full flex-col gap-y-8 sm:w-[540px]"
    >
      <div className="flex flex-col gap-y-8">
        <Input
          id="name"
          label="Course Name"
          placeholder="e.g., Mindfulness for Life"
          {...register("name", { required: "Course name is required" })}
          error={errors.name?.message}
          required
        />

        <TextArea
          id="description"
          label="Description"
          placeholder="Course description..."
          {...register("description", {
            required: "Description is required",
          })}
          error={errors.description?.message}
          required
        />

        <Select
          id="partner_id"
          label="Partner"
          options={partnerOptions}
          {...register("partner_id", { required: "Partner is required" })}
          error={errors.partner_id?.message}
          required
        />

        <MultiSelect
          label="Materials (Optional)"
          placeholder="Select materials..."
          options={materialOptions}
          selectedOptions={selectedMaterials}
          onChange={setSelectedMaterials}
        />

        <ControlledDropzone
          name="image"
          control={control}
          label="Image (Optional)"
          placeholder="Upload course image"
          accept={{ "image/*": [".png", ".jpg", ".jpeg"] }}
        />
      </div>

      <div className="flex justify-center gap-2">
        <Button onClick={onClose} variant="outlined" className="flex-grow">
          Cancel
        </Button>
        <Button
          type="submit"
          className="flex-grow"
          disabled={!isValid || isPending || isUploading}
        >
          {isPending || isUploading ? (
            <Spinner className="h-5 w-5 animate-spin" />
          ) : (
            "Create Course"
          )}
        </Button>
      </div>
    </form>
  );
};
